import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useMemo } from 'react'

import { actions, selectors } from '../../redux'
import { NUMBER_OF_CHILDREN } from '../../types'

import {
  useAcceptTermsStep,
  useAddDeclarationStep,
  useAddMemberStep,
  useCciFunctionStep,
  useDeclarationListStep,
  useIntentStep,
  useSummaryStep,
} from './Steps'
import { useRegistrant, usePartner, useChild } from './Steps/Member'
import { useConfirmStep } from './Steps/useConfirmStep'

export const useDeclarationFormSteps = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const user = useSelector(selectors.auth.user)
  const formValues = useSelector(selectors.declaration.formValues)
  const declarationFunctions = useSelector(selectors.declaration.declarationFunctions)
  const step = useSelector(selectors.declaration.step)

  const registrantProps = useRegistrant({
    t,
    dispatch,
    formValues,
    fullName: `${user?.firstName} ${user?.lastName}`,
  })

  const partnerProps = usePartner({
    t,
    dispatch,
    formValues,
    fullName: `${formValues.partner?.firstName} ${formValues.partner?.lastName}`,
  })

  const childProps = useChild({
    t,
    dispatch,
    formValues,
    fullName: '',
    childIndex: 0,
  })

  useEffect(() => {
    dispatch(
      actions.declaration.functionsRequest({
        first: 15,
      })
    )
    dispatch(actions.declaration.familyRelationshipsRequest({}))
  }, [dispatch])

  useEffect(() => {
    if (user?.cgu_accepted_at && step === 0) {
      dispatch(actions.declaration.setNextStep())
    }
  }, [user, step, dispatch])

  return {
    acceptTerms: useAcceptTermsStep({
      t,
      dispatch,
      formValues,
      user,
    }),
    steps: [
      // Registrant steps
      useCciFunctionStep({
        t,
        dispatch,
        formValues,
        declarationFunctions,
      }),
      useIntentStep({
        t,
        dispatch,
        formValues,
      }),
      useAddDeclarationStep({
        t,
        dispatch,
        formValues,
        ...registrantProps,
      }),
      useDeclarationListStep({
        t,
        dispatch,
        formValues,
        ...registrantProps,
      }),
      // Partner steps
      useAddMemberStep({
        t,
        dispatch,
        formValues,
        ...partnerProps,
      }),
      useAddDeclarationStep({
        t,
        dispatch,
        formValues,
        ...partnerProps,
      }),
      useDeclarationListStep({
        t,
        dispatch,
        formValues,
        ...partnerProps,
      }),
      // Children steps
      useAddMemberStep({
        t,
        dispatch,
        formValues,
        ...childProps,
      }),
      // As I use hooks, I can't loop over children to create steps
      // because the number of children is dynamic so the number of hooks is dynamic too
      // but hooks cannot be created / called dynamically
      ...Array.from(Array(NUMBER_OF_CHILDREN))
        .map((_, i) =>
          ChildSteps({
            t,
            dispatch,
            formValues,
            childIndex: i,
          })
        )
        .flat(),
      useSummaryStep({
        t,
        dispatch,
        formValues,
        registrantName: `${user?.firstName} ${user?.lastName}`,
      }),
      useConfirmStep({
        t,
        dispatch,
        formValues,
        registrantName: `${user?.firstName} ${user?.lastName}`,
      }),
    ],
  }
}

type ChildStepProps = {
  t: any
  dispatch: any
  formValues: any
  childIndex: number
}

export const ChildSteps = (props: ChildStepProps) => {
  const { t, dispatch, formValues, childIndex } = props

  const fullName = useMemo(() => {
    return `${formValues?.children[childIndex]?.firstName} ${formValues?.children[childIndex]?.lastName}`
  }, [formValues, childIndex])

  const childProps = useChild({
    t,
    dispatch,
    formValues,
    fullName,
    childIndex,
  })

  return [
    useAddDeclarationStep({
      ...props,
      ...childProps,
    }),
    // useDeclarationListStep({
    //   ...props,
    //   ...childProps,
    // }),
  ]
}
